import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/context";
import { SERVICE_SLUGS, servicePages } from "@/servicesData";
import { Reveal } from "@/components/Reveal";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const EASE = [0.22, 1, 0.36, 1];

export default function ServicePage() {
  const { slug } = useParams();
  const { lang, t, setService } = useLang();
  const navigate = useNavigate();
  const data = servicePages[lang]?.[slug] || servicePages.en[slug];

  useEffect(() => {
    if (!data) navigate("/", { replace: true });
  }, [data, navigate]);

  if (!data) return null;

  const idx = SERVICE_SLUGS.indexOf(slug);
  const nextSlug = SERVICE_SLUGS[(idx + 1) % SERVICE_SLUGS.length];
  const next = servicePages[lang]?.[nextSlug] || servicePages.en[nextSlug];

  const cta = () => {
    setService(data.contactKey);
    navigate("/contact");
  };

  return (
    <div data-testid={`service-page-${slug}`} className="min-h-screen bg-ivory">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 pt-32 sm:pt-40 pb-28">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          <Link
            to="/#services"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-taupe hover:text-gold transition-colors"
          >
            <ArrowLeft size={13} /> {t.servicePage.back}
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-12 flex items-center gap-4"
        >
          <span className="font-serif text-sm text-gold tracking-[0.2em]">{data.num}</span>
          <span className="gold-rule w-12" aria-hidden="true" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-taupe">{t.nav.services}</span>
        </motion.div>

        <motion.h1
          data-testid="service-page-title"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="mt-8 font-serif text-3xl sm:text-5xl lg:text-6xl uppercase tracking-[0.04em] text-charcoal font-medium leading-[1.12] max-w-4xl"
        >
          {data.title}
        </motion.h1>

        <motion.p
          data-testid="service-page-headline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
          className="mt-10 font-serif italic text-2xl sm:text-3xl lg:text-4xl text-charcoal/90 leading-snug max-w-2xl"
        >
          “{data.headline}”
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
          className="mt-6 max-w-xl text-base sm:text-lg text-ink/75 leading-relaxed font-light"
        >
          {data.intro}
        </motion.p>

        <div className="mt-16 sm:mt-20 grid grid-cols-12 gap-y-12 lg:gap-16">
          <div className="col-span-12 lg:col-span-7 min-w-0">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold mb-8">{t.servicePage.includes}</p>
            </Reveal>
            {data.includes.map((item, i) => (
              <Reveal key={item} delay={i * 0.06} y={16}>
                <div data-testid={`service-include-${i}`} className="group flex items-baseline gap-5 border-t border-taupe/25 py-5">
                  <span className="font-serif text-sm text-gold">0{i + 1}</span>
                  <span className="text-base sm:text-lg text-charcoal/85 font-light">{item}</span>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-taupe/25" />
          </div>

          <div className="col-span-12 lg:col-span-5 min-w-0">
            <Reveal delay={0.15}>
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold">{t.servicePage.experience}</p>
              <p data-testid="service-page-experience" className="mt-4 text-sm sm:text-base text-ink/70 leading-relaxed font-light">
                {data.experience}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-10 text-[11px] uppercase tracking-[0.3em] text-gold">{t.servicePage.why}</p>
              <p data-testid="service-page-why" className="mt-4 font-serif italic text-xl sm:text-2xl text-charcoal/90 leading-snug">
                {data.why}
              </p>
              {data.disclaimer && (
                <p data-testid="service-page-disclaimer" className="mt-5 text-xs text-taupe font-light leading-relaxed max-w-sm">
                  {data.disclaimer}
                </p>
              )}
            </Reveal>
            <Reveal delay={0.35}>
              <button
                data-testid="service-page-cta"
                onClick={cta}
                className="mt-12 bg-charcoal text-ivory border border-charcoal px-10 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-gold hover:border-gold hover:text-charcoal transition-colors duration-300"
              >
                {t.contact.cta}
              </button>
            </Reveal>
          </div>
        </div>

        {data.statement && (
          <Reveal delay={0.1}>
            <p
              data-testid="service-page-statement"
              className="mt-24 font-serif italic text-3xl sm:text-5xl text-charcoal leading-[1.15] max-w-4xl border-l-2 border-gold pl-8"
            >
              “{data.statement}”
            </p>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <Link
            to={`/services/${nextSlug}`}
            data-testid="service-next-link"
            className="group mt-24 flex items-baseline justify-between border-t border-taupe/25 pt-8"
          >
            <span className="text-[11px] uppercase tracking-[0.26em] text-taupe">{t.servicePage.next}</span>
            <span className="flex items-center gap-3 font-serif text-xl sm:text-2xl text-charcoal group-hover:text-gold transition-colors duration-300">
              {next.title} <ArrowRight size={16} className="text-gold" />
            </span>
          </Link>
        </Reveal>
      </div>
      <Footer />
    </div>
  );
}
